/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useRef } from 'react';
import {
  PageSection,
  Filepond,
  EntityTypes,
  SetProfilePicture,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { throwErrorWithId } from '@pypestream/utils';

import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { useProStudioViewerRole } from '../../hooks';
import { AssetCard } from '../../components';
import { readLegacyBotSolutionId, uploadFile } from '../../utils';
import {
  createSerialQueue,
  uploadMediaAssets,
} from '../../utils/load-agent-manager-assets';

import './files.scss';

type Asset = {
  id: string;
  url: string;
  path?: string;
  sha?: string;
  mimeType?: string;
};

/**
 * The media grid, moved verbatim out of the old standalone Files page into a tab. The
 * page title and tooltip now live in `FilesLayout`, so only the body is here.
 *
 * Writes follow the solution's backend. An Agent Manager v2 solution uploads into its
 * repo's `assets/` folder through `agent-manager-sync` (and deletes through
 * `deleteAgentManagerFile`, see delete-asset.ts); a legacy solution — one that still has
 * a `botSolutionId` — keeps writing through Bot Manager. Either way the grid is
 * re-read afterwards through `updateAssets`.
 */
export const MediaTab: FC = () => {
  const [t] = useTranslation();
  const { assets, botId, projectId, isLegacy, solutionId, draftVersion } =
    useProStudioCtxSelector((ctx) => {
      const ctxAssets: Asset[] = ((ctx.selectedBot?.assets || []) as Asset[])[0]
        ?.id
        ? ((ctx.selectedBot?.assets || []) as Asset[])
        : [];

      const { customerId, botName, project } = ctx;

      return {
        botId: customerId && botName ? `${customerId}.${botName}` : undefined,
        assets: ctxAssets,
        projectId: project?.id,
        isLegacy: Boolean(readLegacyBotSolutionId(ctx)),
        solutionId: ctx.selectedBot?.uniqueId,
        draftVersion: ctx.draftVersion,
      };
    });

  const isViewer = useProStudioViewerRole();
  const uploadQueue = useRef(createSerialQueue());

  const onUpload: SetProfilePicture = ({
    entityId,
    file,
    load,
    error,
    progress,
    abort,
    onChange,
  }) => {
    if (!isLegacy) {
      if (!solutionId || !draftVersion) {
        throwErrorWithId('Solution ID and draft version are required');
      }
      const reportUploadError = (e: unknown) => {
        const message = e instanceof Error ? e.message : String(e);
        sendProStudioEvent({ type: 'showError', error: message });
        error?.(message);
      };
      // Filepond calls this once per picked file; each upload is its own
      // commit on the same branch, so they must not race (see createSerialQueue).
      // A file the user cancels while it waits its turn is skipped. One already
      // committing cannot be recalled — the commit is a single request.
      let cancelled = false;
      uploadQueue
        .current(async () => {
          if (cancelled) return false;
          await uploadMediaAssets({
            solutionId: solutionId as string,
            versionName: draftVersion as string,
            files: [file as File],
          });
          return true;
        })
        .then((uploaded) => {
          if (uploaded && !cancelled) load?.('');
        })
        .catch(reportUploadError)
        .finally(() => {
          // Re-read once the batch has landed. The v2 branch of `updateAssets`
          // lists `assets/`, which is what the grid and every
          // `selectedBot.assets` reader render; it is an idle-only event, so a
          // second dispatch mid-read would be dropped.
          if (uploadQueue.current.pending === 0) {
            sendProStudioEvent({ type: 'updateAssets' });
          }
        });
      return {
        abort: () => {
          cancelled = true;
          abort?.();
        },
      };
    }

    if (!projectId || !entityId) {
      throwErrorWithId('Project ID and bot ID are required');
    }

    const uploadUrl = `${
      import.meta.env.FE_ADMIN_URL
    }/rest/bot-manager/${projectId}/${entityId}/assets`;

    return uploadFile({
      url: uploadUrl,
      file: file as File,
      type: 'assetData',
      load,
      progress,
      abort,
      onChange,
      error: (e) => {
        sendProStudioEvent({
          type: 'showError',
          error: e,
        });
        error?.(e);
      },
    });
  };

  return (
    <PageSection test-id="ps-files-page-section" size="small">
      <div className="c-files-page__list">
        {!isViewer && (
          <Filepond
            test-id="ps-files-page-filepond"
            vertical
            allowMultiple
            className="c-files-page__list-item"
            entityType={EntityTypes.ASSET}
            entityId={botId}
            previewLabel={
              t('pro-studio/files:filepond.previewLabel', {
                defaultValue: 'Drag a file here',
              }) || ''
            }
            onUpload={onUpload}
            onChange={(props) => {
              if (!props?.data) {
                return;
              }

              sendProStudioEvent({
                type: 'updateAssets',
                // @ts-ignore
                data: props.data,
              });
            }}
          />
        )}

        {assets.map(({ id, url, mimeType }) => (
          <AssetCard
            testId={`ps-files-page-asset-card-${id}`}
            key={id}
            id={id}
            url={url}
            mimeType={mimeType}
            viewOnly={isViewer}
          />
        ))}
      </div>
    </PageSection>
  );
};
